import { Add16 } from '@carbon/icons-react';
import React, { useEffect, useMemo, useState } from 'react';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import { FormattedMessage } from 'react-intl';
import { TaskAPI } from 'api/tasks';
import { Task } from 'data/tasks';
import { useTasksByIdsFromRQ } from 'data/tasks/hooks';
import { reorder } from 'utils/reorder';
import {
  AddItemButton,
  AddItemWrapper,
  PreTitle,
  SectionName,
  SectionWrapper,
  TitleWrapper,
  AddTask,
} from './components';
import { CpSubTaskCard } from './cp-sub-task-card';

interface Props {
  task: Task;
}

export const CpTaskSubTasksSection: React.FC<Props> = ({ task }) => {
  const { data: subTasksFromRQ, refetch } = useTasksByIdsFromRQ(
    ['project_id', (task?.project as any)?.projectId ?? -1],
    task?.subTaskOrder ?? [],
  );

  const [subTasks, setSubTasks] = useState(subTasksFromRQ);

  const completedSubTasksCount = useMemo(
    () => subTasks.filter((t) => t.completedAt).length,
    [subTasks],
  );

  useEffect(() => {
    const subTaskOrder = task?.subTaskOrder;

    if (subTaskOrder) {
      setSubTasks(
        subTasksFromRQ.sort(
          (a, b) =>
            subTaskOrder.indexOf(a.taskId) - subTaskOrder.indexOf(b.taskId),
        ),
      );
    } else {
      setSubTasks(subTasksFromRQ);
    }
  }, [subTasksFromRQ, task?.subTaskOrder]);

  return (
    <SectionWrapper>
      <PreTitle>
        {completedSubTasksCount} / {task?.subTasks?.length}{' '}
        <FormattedMessage id="sub_tasks" />
      </PreTitle>
      <TitleWrapper>
        <SectionName>
          <FormattedMessage id="sub_tasks" />
        </SectionName>
      </TitleWrapper>
      <DragDropContext
        onDragEnd={(result) => {
          if (!result.destination) {
            return;
          }

          const items = reorder(
            subTasks,
            result.source.index,
            result.destination.index,
          );

          setSubTasks(items);

          TaskAPI.updateSubTaskOrder(task?.taskId, {
            subTaskOrder: items.map((t) => t.taskId),
          });
        }}
      >
        <Droppable
          droppableId={`subtasks-${task?.taskId}`}
          key={`subtasks-${task?.taskId}`}
        >
          {(dropProvided) => (
            <div
              ref={dropProvided.innerRef}
              {...dropProvided.droppableProps}
              key={`subtasks-${task?.taskId}__droppable`}
            >
              {subTasks.map((subTask) => (
                <Draggable
                  key={subTask.taskId}
                  draggableId={`draggable-${subTask.taskId.toString()}`}
                  index={subTasks.indexOf(subTask)}
                >
                  {(dragProvided) => (
                    <div
                      ref={dragProvided.innerRef}
                      {...dragProvided.draggableProps}
                      {...dragProvided.dragHandleProps}
                      key={`subtask-${subTask.taskId}`}
                    >
                      <CpSubTaskCard
                        draggable
                        subTask={subTask}
                        projectId={(task?.project as any)?.projectId}
                        refetch={refetch}
                      />
                    </div>
                  )}
                </Draggable>
              ))}
              {dropProvided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
      <AddItemWrapper withTopMargin>
        <Add16 />
        <AddItemButton>
          <AddTask>
            <FormattedMessage id="sub_tasks.add" />
          </AddTask>
        </AddItemButton>
      </AddItemWrapper>
    </SectionWrapper>
  );
};
